import { inject, Injectable } from '@angular/core';

import { combineLatest, merge, throwError } from 'rxjs';
import { switchMap, take } from 'rxjs/operators';

import { AddonType } from '@core/billing-types';
import { BillingEstimateDataService, BillingPlansDataService } from '@recruitee/billing-logic';

import { ProductOrderParamsIT } from '../pages/product-order/product-order.types';
import { PricingFeatureFacade } from '../types/pricing-feature.types';

@Injectable()
export class ProductOrderEstimateService {
  private readonly billingPlansData = inject(BillingPlansDataService);
  private readonly estimateData = inject(BillingEstimateDataService);
  public estimate$ = this.estimateData.data$;
  private isLoaded$ = this.estimateData.isLoaded$;
  private params$ = inject(ProductOrderParamsIT);

  constructor(private facade: PricingFeatureFacade) {}

  public getData$() {
    return this.isLoaded$.pipe(
      switchMap(isLoaded => (isLoaded ? this.estimate$ : this.loadData$())),
    );
  }

  public loadData$() {
    this.fetch();
    const success = this.estimateData.fetchSuccess$;
    const error = this.estimateData.fetchFailed$.pipe(
      switchMap(action => throwError(() => action.payload.error)),
    );

    return merge(success, error).pipe(take(1));
  }

  private fetch() {
    const load$ = combineLatest({
      params: this.params$,
      activePlan: this.facade.getActiveBillingPlan(),
      billingPlans: this.facade.getBillingPlansWithActive(),
      sizeRange: this.params$.pipe(
        switchMap(params => this.billingPlansData.getRangeForQuantity$(params.quantity)),
      ),
    }).pipe(
      take(1),
      switchMap(({ params, activePlan, billingPlans, sizeRange }) => {
        const { productId, quantity, interval, slots } = params;
        const selectedPlanId = productId;
        const selectedPlanTier = billingPlans.find(plan => plan.id === selectedPlanId)!.tier;
        const selectedPlanCustom = billingPlans.find(plan => plan.id === selectedPlanId)!.custom;
        const selectedPlanPeriod = interval;
        const activePlanTier = activePlan?.tier;
        const isChangingPlan = !activePlan || activePlanTier !== selectedPlanTier;
        const isChangingPeriod = !activePlan || activePlan.billingPeriod !== selectedPlanPeriod;
        const isSwitchingToCustom = !activePlan || (!activePlan.custom && selectedPlanCustom);
        const estimatePlanIdParam =
          isChangingPlan || isChangingPeriod || isSwitchingToCustom
            ? selectedPlanId
            : activePlan?.id;
        const addons =
          quantity !== undefined
            ? []
            : slots !== undefined
              ? [{ type: AddonType.Slots, quantity: slots }]
              : [];

        return this.facade.loadBillingEstimate(interval, estimatePlanIdParam, addons, sizeRange);
      }),
    );
    load$.pipe(take(1)).subscribe();
  }
}
